
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AutocompleteSelect from "@/components/ui/autocomplete-select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";
import { GuestManager, Guest } from "@/components/ui/guest-manager";
import { MapPin, Users, Send, Euro, Heart, Utensils, Camera, Mountain, Palette, MessageSquare, Globe, Waves, TreePine, PawPrint, ShoppingBag, Calendar, Moon, Sparkles, Trophy, Baby, Umbrella } from "lucide-react";
import { toast } from "@/hooks/useToast";
import { useI18n } from '@/hooks/useI18n';
import Loader from "@/components/ui/loader";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";
import { getRandomSample } from "@/fixtures/sampleTripRequest";

import { useLocation } from "react-router-dom";
import { Mail, ArrowLeft } from "lucide-react";

// Types

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface TravelDates {
  startDate: string;
  endDate: string;
  flexible: boolean;
}

interface Budget {
  min: string;
  max: string;
  flexible: boolean;
}

interface FormData {
  destinations: string[];
  departurePoint: string;
  needsTransportation: boolean;
  autoAssignDesigner: boolean; // Nuovo flag per gestire la checkbox
  travelDesigner: string | null; // UUID del designer o null
  totalGuests: number;
  guests: Guest[];
  travelDates: TravelDates;
  budget: Budget;
  interests: string[];
  additionalNotes: string;
  contactInfo: ContactInfo;
  promoCode?: string;
}

// Constants
const interestOptions = [
  { id: "beach", labelKey: "travelRequest.interestBeach" as const, icon: Waves },
  { id: "mountain", labelKey: "travelRequest.interestMountain" as const, icon: Mountain },
  { id: "wildlife", labelKey: "travelRequest.interestWildlife" as const, icon: TreePine },
  { id: "relax", labelKey: "travelRequest.interestRelax" as const, icon: Umbrella },
  { id: "spa", labelKey: "travelRequest.interestSpa" as const, icon: Sparkles },
  { id: "shopping", labelKey: "travelRequest.interestShopping" as const, icon: ShoppingBag },
  { id: "food", labelKey: "travelRequest.interestFood" as const, icon: Utensils },
  { id: "culture", labelKey: "travelRequest.interestCulture" as const, icon: Palette },
  { id: "events", labelKey: "travelRequest.interestEvents" as const, icon: Calendar },
  { id: "nightlife", labelKey: "travelRequest.interestNightlife" as const, icon: Moon },
  { id: "romantic", labelKey: "travelRequest.interestRomantic" as const, icon: Heart },
  { id: "sports", labelKey: "travelRequest.interestSports" as const, icon: Trophy },
  { id: "family", labelKey: "travelRequest.interestFamily" as const, icon: Baby },
  { id: "pet-friendly", labelKey: "travelRequest.interestPetFriendly" as const, icon: PawPrint },
  { id: "photography", labelKey: "travelRequest.interestPhotography" as const, icon: Camera }
];



// Helper Components
const IconInput = ({
  icon: Icon,
  placeholder,
  value,
  onChange,
  type = "text",
  className = "",
  hasError = false
}: {
  icon: React.ComponentType<{ className?: string }>;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  className?: string;
  hasError?: boolean;
}) => (
  <div className="relative">
    <Icon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
    <Input
      type={type}
      placeholder={placeholder}
      className={`pl-12 pr-4 py-3 rounded-xl ${className}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      hasError={hasError}
    />
  </div>
);



const RequestTrip = () => {
  const { user } = useAuth();
  const destinationInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());

  // Prefill travel designer from URL query param `td` (example: /request?td=<designer-id>)
  useEffect(() => {
    const tdId = new URLSearchParams(location.search).get('td');
    if (tdId) {
      updateFormData({ travelDesigner: tdId, autoAssignDesigner: false });
    }
  }, []);

  const [formData, setFormData] = useState<FormData>({
    destinations: [],
    departurePoint: "",
    needsTransportation: false,
    autoAssignDesigner: true, // Di default lascia scegliere a Place2Be
    travelDesigner: null,
    totalGuests: 1,
    guests: [{ id: 1, age: 25 }],
    travelDates: {
      startDate: "",
      endDate: "",
      flexible: false
    },
    budget: {
      min: "",
      max: "",
      flexible: false
    },
    interests: [],
    additionalNotes: "",
    contactInfo: {
      firstName: "",
      lastName: "",
      email: "",
      phone: ""
    },
    promoCode: ""
  });
  const { t } = useI18n();
  const location = useLocation();
  const isFromSite = new URLSearchParams(location.search).get('from') === 'site';

  

  // Helper functions
  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    
    // Clear validation errors for updated fields
    const newErrors = new Set(validationErrors);
    Object.keys(updates).forEach(key => {
      if (key === 'destinations' && updates.destinations && updates.destinations.length > 0) {
        newErrors.delete('destinations');
      } else if (key === 'travelDesigner' && updates.travelDesigner) {
        newErrors.delete('travelDesigner');
      } else if (key === 'autoAssignDesigner') {
        // Se auto-assign è true, rimuovi l'errore di validazione del designer
        if (updates.autoAssignDesigner) {
          newErrors.delete('travelDesigner');
        }
      } else if (key === 'departurePoint' && updates.departurePoint) {
        newErrors.delete('departurePoint');
      }
    });
    setValidationErrors(newErrors);
  };

  const updateNestedField = <K extends keyof FormData>(
    field: K,
    updates: Partial<FormData[K]>
  ) => {
    setFormData(prev => {
      const currentValue = prev[field];
      if (typeof currentValue === 'object' && currentValue !== null) {
        return {
          ...prev,
          [field]: { ...currentValue, ...updates }
        };
      }
      return prev;
    });
    
    // Clear validation errors for updated nested fields
    const newErrors = new Set(validationErrors);
    if (field === 'travelDates') {
      if ('startDate' in updates && updates.startDate) newErrors.delete('travelStartDate');
      if ('endDate' in updates && updates.endDate) newErrors.delete('travelEndDate');
    } else if (field === 'budget') {
      if ('min' in updates && updates.min) newErrors.delete('budgetMin');
      if ('max' in updates && updates.max) newErrors.delete('budgetMax');
    } else if (field === 'contactInfo') {
      if ('firstName' in updates && updates.firstName) newErrors.delete('contactFirstName');
      if ('lastName' in updates && updates.lastName) newErrors.delete('contactLastName');
      if ('email' in updates && updates.email) newErrors.delete('contactEmail');
      if ('phone' in updates && updates.phone) newErrors.delete('contactPhone');
    }
    setValidationErrors(newErrors);
  };

  // Destination functions
  const addDestination = (destination: string) => {
    if (destination && !formData.destinations.includes(destination)) {
      updateFormData({
        destinations: [...formData.destinations, destination]
      });
      // Clear destinations validation error
      const newErrors = new Set(validationErrors);
      newErrors.delete('destinations');
      setValidationErrors(newErrors);
    }
  };

  const removeDestination = (destination: string) => {
    updateFormData({
      destinations: formData.destinations.filter(d => d !== destination)
    });
  };

  const handleAddDestination = () => {
    if (destinationInputRef.current) {
      const value = destinationInputRef.current.value.trim();
      if (value) {
        addDestination(value);
        destinationInputRef.current.value = '';
      }
    }
  };

  // Guest functions
  const addGuest = () => {
    const newGuest = { id: Date.now(), age: 25 };
    updateFormData({
      totalGuests: formData.totalGuests + 1,
      guests: [...formData.guests, newGuest]
    });
  };

  const removeGuest = () => {
    if (formData.guests.length <= 1) return;

    updateFormData({
      totalGuests: formData.totalGuests - 1,
      guests: formData.guests.slice(0, -1)
    });
  };

  const updateGuestAge = (guestId: number, newAge: number) => {
    // Ensure age is always at least 1
    const validAge = Math.max(1, newAge);
    updateFormData({
      guests: formData.guests.map(guest =>
        guest.id === guestId ? { ...guest, age: validAge } : guest
      )
    });
  };

  // Interest functions
  const toggleInterest = (interest: string) => {
    updateFormData({
      interests: formData.interests.includes(interest)
        ? formData.interests.filter(i => i !== interest)
        : [...formData.interests, interest]
    });
  };

  // Funzione per applicare dati di esempio (solo in development)
  const applySampleData = () => {
    const sampleData = getRandomSample();
    setFormData(sampleData);
    toast({
      title: "Dati di esempio applicati! 🎉",
      description: "Il form è stato compilato con dati di esempio per il testing.",
    });
  };

  // Validation function
  const validateForm = () => {
    const errors = new Set<string>();
    
    // Configuration array for validation rules
    const validationRules = [
      {
        key: 'destinations',
        condition: () => formData.destinations.length === 0
      },
      {
        key: 'travelDesigner',
        condition: () => !formData.autoAssignDesigner && !formData.travelDesigner
      },
      {
        key: 'departurePoint',
        condition: () => !formData.departurePoint
      },
      {
        key: 'travelStartDate',
        condition: () => !formData.travelDates.startDate
      },
      {
        key: 'travelEndDate',
        condition: () => !formData.travelDates.endDate
      },
      {
        key: 'budgetMin',
        condition: () => !formData.budget.min
      },
      {
        key: 'budgetMax',
        condition: () => !formData.budget.max
      },
      {
        key: 'contactFirstName',
        condition: () => !formData.contactInfo.firstName
      },
      {
        key: 'contactLastName',
        condition: () => !formData.contactInfo.lastName
      },
      {
        key: 'contactEmail',
        condition: () => !formData.contactInfo.email
      },
      {
        key: 'contactPhone',
        condition: () => !formData.contactInfo.phone
      }
    ];
    
    // Apply validation rules
    validationRules.forEach(rule => {
      if (rule.condition()) {
        errors.add(rule.key);
      }
    });
    
    setValidationErrors(errors);
    return errors.size === 0;
  };

  // Submit function
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Clear previous validation errors
      setValidationErrors(new Set());
      
      // Validate form
      if (!validateForm()) {
        toast({
          title: t('travelRequest.requiredFieldsMissing'),
          description: t('travelRequest.fillAllRequiredFields'),
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      const { data, error } = await supabase
        .from('travel_requests')
        .insert({
          user_id: user?.id || null, // Ora può essere null per richieste anonime
          destinations: formData.destinations,
          departure_point: formData.departurePoint,
          needs_transportation: formData.needsTransportation,
          total_guests: formData.totalGuests,
          guests_data: formData.guests as unknown as Json, // Convert to JSON format
          travel_dates_flexible: formData.travelDates.flexible,
          travel_start_date: formData.travelDates.startDate,
          travel_end_date: formData.travelDates.endDate,
          budget_min: parseFloat(formData.budget.min),
          budget_max: parseFloat(formData.budget.max),
          budget_flexible: formData.budget.flexible,
          interests: formData.interests.length > 0 ? formData.interests : null,
          additional_notes: formData.additionalNotes || null,
          contact_first_name: formData.contactInfo.firstName,
          contact_last_name: formData.contactInfo.lastName,
          contact_email: formData.contactInfo.email,
          contact_phone: formData.contactInfo.phone,
          travel_designer_preference: formData.autoAssignDesigner ? null : (formData.travelDesigner || null),
          assigned_designer_id: formData.travelDesigner || null, // Auto-assegnazione da parametro URL 'td' o selezione manuale
          status: 'pending',
          promo_code: formData.promoCode || null
        });

      if (error) {
        toast({
          title: t('travelRequest.submitError'),
          description: error.message,
          variant: "destructive",
        });
        return;
      } else {
        toast({
          title: t('travelRequest.requestSent'),
          description: t('travelRequest.travelAdvisorPerfectMatchDesc'),
        });
      }


      // Show confirmation
      if (isFromSite) {
        window.location.href = 'https://www.place2be.it';
        return;
      }
      setShowConfirmation(true);

      // Reset form
      setFormData({
        destinations: [],
        departurePoint: "",
        needsTransportation: false,
        autoAssignDesigner: true,
        travelDesigner: null,
        totalGuests: 1,
        guests: [{ id: 1, age: 25 }],
        travelDates: {
          startDate: "",
          endDate: "",
          flexible: true
        },
        budget: {
          min: "",
          max: "",
          flexible: true
        },
        interests: [],
        additionalNotes: "",
        contactInfo: {
          firstName: "",
          lastName: "",
          email: "",
          phone: ""
        }
      });
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: t('travelRequest.unexpectedError'),
        description: t('travelRequest.unexpectedErrorDesc'),
        variant: "destructive",
      });
      // showDevError({ error, context: 'unexpected error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show confirmation if form was submitted
  if (showConfirmation) {
    return (
      <div className="min-h-screen bg-white text-gray-900 h-full flex items-center justify-center">
        <Card className="border border-gray-200 rounded-2xl w-full max-w-lg">
          <CardContent className="text-center p-8">
            <div className="flex flex-col items-center space-y-8">
              {/* Icon */}
              <div className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center">
                <Mail className="w-7 h-7 text-white" />
              </div>

              {/* Title */}
              <div className="space-y-2">
                <h1 className="text-gray-900">
                  {t('travelRequest.thankYouTitle')}
                </h1>
                <p className="text-gray-600">
                  {t('travelRequest.thankYouSubtitle')}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center space-y-5 mt-10">
              <Button
                className="bg-gray-900 hover:bg-gray-800 text-white rounded-full"
                onClick={() => setShowConfirmation(false)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('travelRequest.planAnotherTrip')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col items-center my-8">
          <div className="flex items-center mb-4">
            <h1>
              {t('travelRequest.planYourStay')}
            </h1>
          </div>
          <p className="text-lg">
            {t('travelRequest.formSubtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Destinations Section */}
          <Card className="border border-gray-200 rounded-2xl">
            <CardContent className="p-6 space-y-8">
              <div className="space-y-4">
                <div>
                  <h4 className="mb-2">
                    {t('travelRequest.whereDoYouWantToGo')} <span className="text-red-500">*</span>
                  </h4>
                  <p className="text-gray-600">
                    {t('travelRequest.addOneOrMoreDestinations')}
                  </p>
                </div>

                {formData.destinations.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.destinations.map((destination, index) => (
                      <Badge
                        key={index}
                        variant="secondary"
                        className="px-4 py-1 rounded-full border bg-white border-gray-200 hover:bg-gray-50 cursor-pointer"
                        onClick={() => removeDestination(destination)}
                      >
                        {destination}
                        <span className="ml-2 text-lg text-gray-500">×</span>
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex gap-4">
                  <div className="w-full lg:w-1/2 relative">
                    <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      ref={destinationInputRef}
                      placeholder={t('travelRequest.destinationPlaceholder')}
                      className="pl-12 pr-4 py-3 rounded-xl"
                      hasError={validationErrors.has('destinations')}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return;
                        e.preventDefault();
                        handleAddDestination();
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="px-6 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                    onClick={handleAddDestination}
                  >
                    {t('travelRequest.addDestination')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trip Details Section */}
          <div className="space-y-6">
            <h3 className="text-center">
              {t('travelRequest.tripDetailsHeading')}
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Departure Point Section */}
              <Card className="border border-gray-200 rounded-2xl">
                <CardContent className="p-6 space-y-8">
                  <div className="space-y-8">
                    <h4>
                      {t('travelRequest.whereAreYouDepartingFrom')} <span className="text-red-500">*</span>
                    </h4>

                    <div className="space-y-8">
                      <IconInput
                        icon={MapPin}
                        placeholder={t('travelRequest.enterDepartureCity')}
                        value={formData.departurePoint}
                        onChange={(value) => updateFormData({ departurePoint: value })}
                        hasError={validationErrors.has('departurePoint')}
                      />

                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="transportation"
                          checked={formData.needsTransportation}
                          onCheckedChange={(checked) => updateFormData({ needsTransportation: checked as boolean })}
                        />
                        <Label htmlFor="transportation" className="font-normal">
                          {t('travelRequest.needsTransportationLabel')}
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* When Section */}
              <Card className="border border-gray-200 rounded-2xl">
                <CardContent className="p-6 space-y-8">
                  <h4>{t('travelRequest.when')} <span className="text-red-500">*</span></h4>

                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={!formData.travelDates.flexible ? "default" : "outline"}
                        className={`px-6 py-2 rounded-full ${!formData.travelDates.flexible ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200'}`}
                        onClick={() => updateNestedField('travelDates', { flexible: false })}
                      >
                        {t('travelRequest.exactDates')}
                      </Button>
                      <Button
                        type="button"
                        variant={formData.travelDates.flexible ? "default" : "outline"}
                        className={`px-6 py-2 rounded-full ${formData.travelDates.flexible ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200'}`}
                        onClick={() => updateNestedField('travelDates', { flexible: true })}
                      >
                        {t('travelRequest.flexible')}
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <DatePicker
                        value={formData.travelDates.startDate}
                        onSelect={(date) => updateNestedField('travelDates', { startDate: date })}
                        placeholder={t('travelRequest.startDate')}
                        hasError={validationErrors.has('travelStartDate')}
                        minDate={new Date()}
                      />
                      <DatePicker
                        value={formData.travelDates.endDate}
                        onSelect={(date) => updateNestedField('travelDates', { endDate: date })}
                        placeholder={t('travelRequest.endDate')}
                        hasError={validationErrors.has('travelEndDate')}
                        minDate={formData.travelDates.startDate ? new Date(formData.travelDates.startDate) : new Date()}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Budget Section */}
              <Card className="border border-gray-200 rounded-2xl">
                <CardContent className="p-6 space-y-8">
                  <h4>{t('travelRequest.budgetPerPerson')} <span className="text-red-500">*</span></h4>

                  <div className="space-y-8">
                    <div className="grid grid-cols-2 gap-4">
                      <IconInput
                        icon={Euro}
                        placeholder={t('travelRequest.budgetMin')}
                        value={formData.budget.min}
                        onChange={(value) => updateNestedField('budget', { min: value })}
                        type="number"
                        hasError={validationErrors.has('budgetMin')}
                      />
                      <IconInput
                        icon={Euro}
                        placeholder={t('travelRequest.budgetMax')}
                        value={formData.budget.max}
                        onChange={(value) => updateNestedField('budget', { max: value })}
                        type="number"
                        hasError={validationErrors.has('budgetMax')}
                      />
                    </div>

                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="flexible-budget"
                        checked={formData.budget.flexible}
                        onCheckedChange={(checked) => updateNestedField('budget', { flexible: checked as boolean })}
                      />
                      <Label htmlFor="flexible-budget" className="font-normal">
                        {t('travelRequest.canIncreaseBudget')}
                      </Label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Notes Section */}
              <Card className="border border-gray-200 rounded-2xl">
                <CardContent className="p-6 space-y-8">
                  <h4 className="text-gray-900">{t('travelRequest.additionalNotesQuestion')}</h4>

                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 text-gray-400 w-5 h-5" />
                    <Textarea
                      placeholder={t('travelRequest.additionalNotesPlaceholder')}
                      className="pl-12 pr-4 py-4 border border-gray-200 rounded-xl resize-none min-h-[130px]"
                      value={formData.additionalNotes}
                      onChange={(e) => updateFormData({ additionalNotes: e.target.value })}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Who Section */}
            <Card className="border border-gray-200 rounded-2xl">
              <CardContent className="p-6 space-y-8">
                <h4>{t('travelRequest.whoQuestion')} <span className="text-red-500">*</span></h4>
                <GuestManager
                  totalGuests={formData.totalGuests}
                  guests={formData.guests}
                  onAddGuest={addGuest}
                  onRemoveGuest={removeGuest}
                  onUpdateGuestAge={updateGuestAge}
                />
              </CardContent>
            </Card>

            {/* Interests Section */}
            <Card className="border border-gray-200 rounded-2xl">
              <CardContent className="p-6 space-y-8">
                <h4 className="text-gray-900">{t('travelRequest.whatDoYouEnjoy')}</h4>

                <div className="flex flex-wrap gap-3">
                  {interestOptions.map((interest) => {
                    const Icon = interest.icon;
                    const isSelected = formData.interests.includes(interest.id);

                    return (
                      <Button
                        key={interest.id}
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        className={`px-4 py-2 rounded-full border ${isSelected
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        onClick={() => toggleInterest(interest.id)}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {t(interest.labelKey)}
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Details Section */}
          <div className="space-y-6">
            <h3 className="text-center">
              {t('travelRequest.aboutYourselfHeading')}
            </h3>

            <Card className="border border-gray-200 rounded-2xl">
              <CardContent className="p-6 space-y-8">
                <h4>{t('travelRequest.howCanWeReachYou')} <span className="text-red-500">*</span></h4>

                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <IconInput
                      icon={Users}
                      placeholder={t('travelRequest.firstNamePlaceholder')}
                      value={formData.contactInfo.firstName}
                      onChange={(value) => updateNestedField('contactInfo', { firstName: value })}
                      hasError={validationErrors.has('contactFirstName')}
                    />
                    <IconInput
                      icon={Users}
                      placeholder={t('travelRequest.lastNamePlaceholder')}
                      value={formData.contactInfo.lastName}
                      onChange={(value) => updateNestedField('contactInfo', { lastName: value })}
                      hasError={validationErrors.has('contactLastName')}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <IconInput
                      icon={MessageSquare}
                      placeholder={t('travelRequest.emailAddressPlaceholder')}
                      value={formData.contactInfo.email}
                      onChange={(value) => updateNestedField('contactInfo', { email: value })}
                      type="email"
                      hasError={validationErrors.has('contactEmail')}
                    />
                    <IconInput
                      icon={MessageSquare}
                      placeholder={t('travelRequest.phoneNumberPlaceholder')}
                      value={formData.contactInfo.phone}
                      onChange={(value) => updateNestedField('contactInfo', { phone: value })}
                      type="tel"
                      hasError={validationErrors.has('contactPhone')}
                    />
                  </div>

                  {/* Promo Code Field */}
                  <div className="grid grid-cols-1">
                    <Label htmlFor="promo-code" className="font-medium">{t('travelRequest.promoCodeLabel')}</Label>
                    <Input
                      id="promo-code"
                      placeholder={t('travelRequest.promoCodePlaceholder')}
                      value={formData.promoCode || ""}
                      onChange={e => updateFormData({ promoCode: e.target.value })}
                      className="mt-2"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Submit Button */}
          <div className="text-center pt-8">
            <Button
              type="submit"
              size="lg"
              className="bg-gray-900 hover:bg-gray-800 text-white px-12 py-4 rounded-xl"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader size="md" className="mr-2" />
                  {t('common.sending')}
                </>
              ) : (
                <>
                  <Send className="mr-2 w-5 h-5" />
                  {t('travelRequest.submitTripRequest')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestTrip;
